import { formatDate } from "features/discover/functions";
import { BooleanChoices } from "features/discover/types";
import type { SubscriptionRowResponse } from "libs/apiClient";
import {
	DatagridBackendFilterType,
	DatagridFilterInputType,
	type ColumnConfig,
	type FilterConfig,
} from "../types";

export type SubscriptionRow = SubscriptionRowResponse;

function formatTrueKeys(value: unknown): string {
	if (!value || typeof value !== "object") {
		return "-";
	}

	const keys = Object.entries(value as Record<string, boolean>)
		.filter(([, enabled]) => enabled === true)
		.map(([key]) => key);

	return keys.length > 0 ? keys.join(", ") : "-";
}

export const subscriptionsColumns: ColumnConfig<SubscriptionRow>[] = [
	{
		key: "id",
		label: "ID",
		sortable: true,
		sortField: "id",
		cell: (value) => (value !== null && value !== undefined ? String(value) : "-"),
		group: "Overview",
		defaultVisible: true,
		width: 80,
	},
	{
		key: "product",
		label: "Product",
		sortable: true,
		sortField: "product",
		cell: (value) => (value as string) || "-",
		group: "Overview",
		defaultVisible: true,
		width: 140,
	},
	{
		key: "proClubName",
		label: "Club",
		sortable: true,
		sortField: "proClubName",
		cell: (value) => (value as string) || "-",
		group: "Overview",
		defaultVisible: true,
		width: 160,
	},
	{
		key: "countriesCount",
		label: "Countries",
		sortable: false,
		cell: (value) => (value !== null && value !== undefined ? String(value) : "-"),
		group: "Overview",
		defaultVisible: true,
		width: 100,
	},
	{
		key: "maxAccounts",
		label: "Max accounts",
		sortable: true,
		sortField: "maxAccounts",
		cell: (value) => (value !== null && value !== undefined ? String(value) : "-"),
		group: "Overview",
		defaultVisible: true,
		width: 110,
	},
	{
		key: "active",
		label: "Active",
		sortable: false,
		cell: (value) => (
			<span>
				{value ? "Active" : "Inactive"}{" "}
				<span className={`status__circle ${value ? "status__circle--ok" : "status__circle--nook"}`} />
			</span>
		),
		group: "Overview",
		defaultVisible: true,
		width: 100,
	},
	{
		key: "pipedriveDealId",
		label: "Pipedrive deal ID",
		sortable: false,
		cell: (value) => (value as string) || "-",
		group: "Overview",
		defaultVisible: true,
		width: 160,
	},
	{
		key: "startDate",
		label: "Start date",
		sortable: true,
		sortField: "startDate",
		cell: (value) => formatDate(value as string) ?? "-",
		group: "Dates",
		defaultVisible: true,
		width: 110,
	},
	{
		key: "expirationDate",
		label: "Expiration date",
		sortable: true,
		sortField: "expirationDate",
		cell: (value) => formatDate(value as string) ?? "-",
		group: "Dates",
		defaultVisible: true,
		width: 130,
	},
	{
		key: "expiresIn",
		label: "Expires in",
		sortable: true,
		sortField: "expirationDate",
		cell: (value) => (value !== null && value !== undefined ? String(value) : "-"),
		group: "Dates",
		defaultVisible: true,
		width: 100,
	},
	{
		key: "teamGroupName",
		label: "Team group",
		sortable: false,
		cell: (value) => (value as string) || "-",
		group: "Details",
		defaultVisible: false,
		width: 160,
	},
	{
		key: "competitionNames",
		label: "Competition templates",
		sortable: false,
		cell: (value) => {
			if (!Array.isArray(value) || value.length === 0) {
				return "-";
			}
			return value.join(", ");
		},
		group: "Details",
		defaultVisible: false,
		width: 220,
	},
	{
		key: "competitionEditionNames",
		label: "Competition editions",
		sortable: false,
		cell: (value) => {
			if (!Array.isArray(value) || value.length === 0) {
				return "-";
			}
			return value.join(", ");
		},
		group: "Details",
		defaultVisible: false,
		width: 240,
	},
	{
		key: "gameOptions",
		label: "Game options",
		sortable: false,
		cell: (value) => formatTrueKeys(value),
		group: "Details",
		defaultVisible: false,
		width: 220,
	},
	{
		key: "eyeballAddons",
		label: "Addons",
		sortable: false,
		cell: (value) => formatTrueKeys(value),
		group: "Details",
		defaultVisible: false,
		width: 180,
	},
];

export const subscriptionsFilters: FilterConfig[] = [
	{
		id: "product",
		field: "product",
		label: "Product",
		type: DatagridFilterInputType.Multiselect,
		filterType: DatagridBackendFilterType.Inarray,
		placeholder: "Products",
	},
	{
		id: "proClubId",
		field: "proClubId",
		label: "Club",
		type: DatagridFilterInputType.Multiselect,
		filterType: DatagridBackendFilterType.Inarray,
		placeholder: "Clubs",
	},
	{
		id: "teamGroupId",
		field: "teamGroupId",
		label: "Team group",
		type: DatagridFilterInputType.Multiselect,
		filterType: DatagridBackendFilterType.Inarray,
		placeholder: "Team groups",
	},
	{
		id: "competitionId",
		field: "competitionId",
		label: "Competition template",
		type: DatagridFilterInputType.Multiselect,
		filterType: DatagridBackendFilterType.Inarray,
		placeholder: "Competition templates",
		wideMenu: true,
	},
	{
		id: "competitionEditionId",
		field: "competitionEditionId",
		label: "Competition edition",
		type: DatagridFilterInputType.Multiselect,
		filterType: DatagridBackendFilterType.Inarray,
		placeholder: "Competition editions",
		wideMenu: true,
	},
	{
		id: "gameOptions",
		field: "gameOptions",
		label: "Game options",
		type: DatagridFilterInputType.Multiselect,
		filterType: DatagridBackendFilterType.Inarray,
		placeholder: "Game options",
		wideMenu: true,
	},
	{
		id: "eyeballAddons",
		field: "eyeballAddons",
		label: "Eyeball addons",
		type: DatagridFilterInputType.Multiselect,
		filterType: DatagridBackendFilterType.Inarray,
		placeholder: "Eyeball addons",
		wideMenu: true,
	},
	{
		id: "active",
		field: "active",
		label: "Active",
		type: DatagridFilterInputType.Boolean,
		filterType: DatagridBackendFilterType.Boolean,
		options: BooleanChoices,
		placeholder: "Active",
	},
	{
		id: "startDate",
		field: "startDate",
		label: "Start date",
		type: DatagridFilterInputType.Daterange,
		filterType: DatagridBackendFilterType.Datetimerange,
	},
	{
		id: "expirationDate",
		field: "expirationDate",
		label: "Expiration date",
		type: DatagridFilterInputType.Daterange,
		filterType: DatagridBackendFilterType.Datetimerange,
	},
	{
		id: "pipedriveDealId",
		field: "pipedriveDealId",
		label: "Pipedrive deal ID",
		type: DatagridFilterInputType.Text,
		filterType: DatagridBackendFilterType.Search,
		placeholder: "Pipedrive deal ID",
	},
];
